// src/components/PendentLoadDetails.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaIndustry, FaTruck, FaCalendarAlt, FaClock } from "react-icons/fa";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import axios from "../axiosConfig";
import "../styles/PendentLoadDetails.css";

/* ===== NEW: helpers for weekday + working hours ===== */
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function weekdayKeyFromISO(isoDateStr) {
  if (!isoDateStr) return null;
  const d = new Date(isoDateStr);
  if (Number.isNaN(d)) return null;
  return DAYS[d.getDay()];
}
function parseWorkingHours(wh) {
  if (!wh) return null;
  if (typeof wh === "string") {
    try { return JSON.parse(wh); } catch { return null; }
  }
  if (typeof wh === "object") return wh;
  return null;
}
function formatIntervalsForDay(workingHours, dayKey) {
  if (!workingHours || !dayKey) return null;
  const day = workingHours[dayKey];
  if (!Array.isArray(day)) return null;
  if (day.length === 0) return "Closed";
  return day.map(([start, end]) => `${start}–${end}`).join(", ");
}
/* ===== END NEW ===== */

const formatDate = (isoDateStr) => {
  if (!isoDateStr) return "N/A";
  const [year, month, day] = isoDateStr.split("-");
  return `${day}/${month}/${year}`;
};

const formatCurrency = (v, { locale = "pt-PT", currency = "EUR" } = {}) => {
  if (v == null || v === "") return "N/A";
  const n = Number(v);
  if (!Number.isFinite(n)) return "N/A";
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(n);
  } catch {
    return `${n.toFixed(2)} €`;
  }
};

const PendentLoadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Se vieste da lista, devemos ter prefill aqui
  const prefill = location.state?.prefill ?? null;

  const [pendentLoad, setPendentLoad] = useState(null);
  const [error, setError] = useState("");
  const [showMorePickupFields, setShowMorePickupFields] = useState({});
  const [showMoreDeliveryFields, setShowMoreDeliveryFields] = useState({});
  const [hasRedirected, setHasRedirected] = useState(false); // guard anti-loop

  // Preenche com prefill se vier da lista, sem navegar
  useEffect(() => {
    if (prefill && prefill.id) {
      setPendentLoad(prefill);
    }
  }, [prefill]);

  // Fallback: sem prefill, carrega os detalhes e mostra-os (sem navegar)
  useEffect(() => {
    if (prefill) return;
    let alive = true;

    (async () => {
      try {
        const { data } = await axios.get(`/pendentloads/${id}`);
        if (!alive) return;
        setPendentLoad(data);
        } catch (err) {
          const status = err?.response?.status;
          const msg = err?.response?.data?.error || err?.message || "Unknown error";
          console.error(`❌ Error fetching pendent load [${status || "no-status"}]:`, msg);
          setError(`Failed to load pendent load details (${status || "ERR"}).`);
        }
    })();

    return () => { alive = false; };
  }, [id, prefill]);

  // Abaixo fica o render antigo como “fallback”, mas na prática quase nunca verás isto
  if (error) return <div className="error-message">{error}</div>;
  if (!pendentLoad) return <div className="loading-message">Opening editor…</div>;

  const pickups = pendentLoad.pickupDeliveries || [];
  const deliveries = pendentLoad.deliveryDeliveries || [];

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB");
  };

  // Mostra location em 2 linhas (title + subtitle)
  const PlaceReadOnly = ({ point }) => {
    const title = point.location || "";
    const city =
      point.city ??
      point.place?.city ??
      "";
    const country =
      point.country ??
      point.place?.country ??
      "";
    const sub = [city, country].filter(Boolean).join(", ");

    return (
      <div className="place-display">
        <div className="place-display__title">{title}</div>
        {sub ? <div className="place-display__subtitle">{sub}</div> : null}
      </div>
    );
  };
  
  
  return (
    <div className="pendent-load-details-container">
      <div
        className="pendent-load-details-back-arrow"
        onClick={() => navigate("/loads?view=pendent")}
      >
        ← Back
      </div>
      <h1 className="pendent-load-details-title">Pendent Load Details</h1>
        <div className="pendent-load-details-card">
  
        {/* Header: ID à esquerda + badge centrado */}
          <div className="pendent-load-details-header">
            <div className="header-left">
              Pendent Load ID: <strong>{pendentLoad?.pendentLoadNumber ?? pendentLoad?.id ?? "N/A"}</strong>
            </div>

            <div className="pendent-load-details-type-labels header-center">
              {pendentLoad?.loadBearer && (
                <div className="pendent-load-details-type-label pendent-load-bearer">Load Bearer</div>
              )}
              {pendentLoad?.specialLoad && (
                <div className="pendent-load-details-type-label pendent-special-load">Special Load</div>
              )}
            </div>

            {/* Spacer para permitir centrar perfeito do badge */}
            <div className="header-right" />
          </div>
  
          <div className="pendent-load-details-columns">
            {/* Column 1: Loading Points */}
            <div className="pendent-load-details-column">
              <h3 className="pendent-load-details-subtitle">LOADING POINTS</h3>
              {pickups.length === 0 ? (
                <p>None</p>
              ) : (
                pickups.map((point, index) => (
                  <div key={index} className="pendent-load-details-field-group">
                    <span className="pendent-load-details-label">Pick-Up Place</span>
                    <PlaceReadOnly point={point} />

                    <div className="pendent-load-details-datetime-group">
                      <div className="pendent-load-details-icon-input">
                        <FaCalendarAlt className="pendent-load-details-input-icon" />
                        <input
                          type="text"
                          value={formatDisplayDate(point.date)}
                          readOnly
                          className="pendent-load-details-input with-icon"
                        />
                      </div>
                      <div className="pendent-load-details-icon-input">
                        <FaClock className="pendent-load-details-input-icon" />
                        <input
                          type="text"
                          value={point.hour || ""}
                          readOnly
                          className="pendent-load-details-input with-icon"
                        />
                      </div>
                    </div>

                    {showMorePickupFields[index] ? (
                      <>
                        <div className="pendent-load-details-extra-fields">
                          {/* ===== NEW: Working hours badge (Pickups) ===== */}
                          {(() => {
                            const dayKey = weekdayKeyFromISO(point.date);
                            const whObj = parseWorkingHours(point.workingHours ?? point.place?.workingHours);
                            const text = formatIntervalsForDay(whObj, dayKey);
                            return (
                              <div className="pendent-load-details-working-hours-badge">
                                <span className="pendent-load-details-working-hours-day">
                                  {dayKey ?? "—"}
                                </span>
                                <span className="pendent-load-details-working-hours-text">
                                  {text ?? "No working hours info"}
                                </span>
                              </div>
                            );
                          })()}
                          {/* ===== END NEW ===== */}
                          <input
                            type="text"
                            value={point.address || ""}
                            readOnly
                            className="pendent-load-details-input"
                            placeholder="Address"
                          />

                          <div className="pendent-load-details-row-group">
                            <input
                              type="text"
                              value={point.postalCode || ""}
                              readOnly
                              className="pendent-load-details-input small"
                              placeholder="Postal Code"
                            />
                            <input
                              type="text"
                              value={point.city || ""}
                              readOnly
                              className="pendent-load-details-input small"
                              placeholder="City"
                            />
                            <input
                              type="text"
                              value={point.country || ""}
                              readOnly
                              className="pendent-load-details-input small"
                              placeholder="Country"
                            />
                          </div>
                          <input
                            type="text"
                            value={point.gMapsLink || ""}
                            readOnly
                            className="pendent-load-details-input"
                            placeholder="Google Maps Link"
                          />
                          <input
                            type="text"
                            value={point.coordinates || ""}
                            readOnly
                            className="pendent-load-details-input"
                            placeholder="Coordinates"
                          />
                          <input
                            type="text"
                            value={point.contactPhone || ""}
                            readOnly
                            className="pendent-load-details-input"
                            placeholder="Contact Phone"
                          />
                        </div>

                        <div className="pendent-load-details-toggle-fields-wrapper">
                          <button
                            type="button"
                            className="pendent-load-details-toggle-fields-btn"
                            onClick={() =>
                              setShowMorePickupFields((prev) => ({ ...prev, [index]: false }))
                            }
                          >
                            Hide fields <FiChevronUp className="pendent-load-details-toggle-arrow" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="pendent-load-details-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="pendent-load-details-toggle-fields-btn"
                          onClick={() =>
                            setShowMorePickupFields((prev) => ({ ...prev, [index]: true }))
                          }
                        >
                          + More fields <FiChevronDown className="pendent-load-details-toggle-arrow" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Column 2: Merchandise & Client */}
            <div className="pendent-load-details-column">
              <h3 className="pendent-load-details-subtitle-merchandise">MERCHANDISE</h3>
              <div className="pendent-load-details-merchandise-row">
                <div className="pendent-load-details-item">
                  <span className="pendent-load-details-label-merchandise">Length (m)</span>
                  <input
                    type="text"
                    value={pendentLoad.length || "N/A"}
                    readOnly
                    className="pendent-load-details-input"
                  />
                </div>
                <div className="pendent-load-details-item">
                  <span className="pendent-load-details-label-merchandise">Height (m)</span>
                  <input
                    type="text"
                    value={pendentLoad.height || "N/A"}
                    readOnly
                    className="pendent-load-details-input"
                  />
                </div>
                <div className="pendent-load-details-item">
                  <span className="pendent-load-details-label-merchandise">Width (m)</span>
                  <input
                    type="text"
                    value={pendentLoad.width || "N/A"}
                    readOnly
                    className="pendent-load-details-input"
                  />
                </div>
              </div>
  
              <h3 className="pendent-load-details-subtitle">GENERAL INFORMATION</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div className="pendent-load-details-item">
                  <span className="pendent-load-details-label">Price</span>
                  <input
                    type="text"
                    readOnly
                    value={formatCurrency(pendentLoad.price)}
                    className="pendent-load-details-input"
                  />
                </div>

                {/* Notes for Driver (optional) */}
                <div className="pendent-load-details-item">
                  <span className="pendent-load-details-label">Notes for Driver</span>
                  <textarea
                    readOnly
                    rows={4}
                    value={
                      typeof pendentLoad?.notes4driver === "string" && pendentLoad.notes4driver.trim()
                        ? pendentLoad.notes4driver
                        : ""
                    }
                    placeholder="No notes for the driver"
                    className="pendent-load-details-textarea"
                  />
                </div>

                {/* Client Type */}
                <div className="pendent-load-details-item">
                  <span className="pendent-load-details-label">Client Type</span>
                  <input
                    type="text"
                    readOnly
                    value={
                      pendentLoad.industryClientId
                        ? "Industry"
                        : pendentLoad.carrierClientId
                        ? "Carrier"
                        : "N/A"
                    }
                    className="pendent-load-details-input"
                  />
                </div>

                {/* Client Name (denorm) */}
                <div className="pendent-load-details-item">
                  <span className="pendent-load-details-label">
                    {pendentLoad.industryClientId
                      ? "Industry Client"
                      : pendentLoad.carrierClientId
                      ? "Carrier Client"
                      : "Client"}
                  </span>
                  <div className="pendent-load-details-client-display">
                    {pendentLoad.industryClientId && pendentLoad.industryClientName ? (
                      <div className="pendent-load-details-client-info">
                        <FaIndustry className="pendent-load-details-client-icon" />
                        <input type="text" readOnly value={pendentLoad.industryClientName} />
                      </div>
                    ) : null}

                    {pendentLoad.carrierClientId && pendentLoad.carrierClientName ? (
                      <div className="pendent-load-details-client-info">
                        <FaTruck className="pendent-load-details-client-icon" />
                        <input type="text" readOnly value={pendentLoad.carrierClientName} />
                      </div>
                    ) : null}

                    {!pendentLoad.industryClientId &&
                      !pendentLoad.carrierClientId && <span>N/A</span>}
                  </div>

                  {/* Transport Carrier (denorm) */}
                  <div className="pendent-load-details-item">
                    <span className="pendent-load-details-label">Transport Carrier</span>
                    <div className="pendent-load-details-client-display">
                      {pendentLoad.carrierId && pendentLoad.carrierName ? (
                        <div className="pendent-load-details-client-info">
                          <FaTruck className="pendent-load-details-client-icon" />
                          <input type="text" readOnly value={pendentLoad.carrierName} />
                        </div>
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Column 3: Unloading Points */}
            <div className="pendent-load-details-column">
              <h3 className="pendent-load-details-subtitle">UNLOADING POINTS</h3>
              {deliveries.length === 0 ? (
                <p>None</p>
              ) : (
                deliveries.map((point, index) => (
                  <div key={index} className="pendent-load-details-field-group">
                    <span className="pendent-load-details-label">Delivery Place</span>
                    <PlaceReadOnly point={point} />

                    <div className="pendent-load-details-datetime-group">
                      <div className="pendent-load-details-icon-input">
                        <FaCalendarAlt className="pendent-load-details-input-icon" />
                        <input
                          type="text"
                          value={formatDisplayDate(point.date)}
                          readOnly
                          className="pendent-load-details-input with-icon"
                        />
                      </div>
                      <div className="pendent-load-details-icon-input">
                        <FaClock className="pendent-load-details-input-icon" />
                        <input
                          type="text"
                          value={point.hour || ""}
                          readOnly
                          className="pendent-load-details-input with-icon"
                        />
                      </div>
                    </div>

                    {showMoreDeliveryFields[index] ? (
                      <>
                        <div className="pendent-load-details-extra-fields">
                          {/* ===== NEW: Working hours badge (Deliveries) ===== */}
                          {(() => {
                            const dayKey = weekdayKeyFromISO(point.date);
                            const whObj = parseWorkingHours(point.workingHours ?? point.place?.workingHours);
                            const text = formatIntervalsForDay(whObj, dayKey);
                            return (
                              <div className="pendent-load-details-working-hours-badge">
                                <span className="pendent-load-details-working-hours-day">
                                  {dayKey ?? "—"}
                                </span>
                                <span className="pendent-load-details-working-hours-text">
                                  {text ?? "No working hours info"}
                                </span>
                              </div>
                            );
                          })()}
                          {/* ===== END NEW ===== */}
                          <input
                            type="text"
                            value={point.address || ""}
                            readOnly
                            className="pendent-load-details-input"
                            placeholder="Address"
                          />

                          <div className="pendent-load-details-row-group">
                            <input
                              type="text"
                              value={point.postalCode || ""}
                              readOnly
                              className="pendent-load-details-input small"
                              placeholder="Postal Code"
                            />
                            <input
                              type="text"
                              value={point.city || ""}
                              readOnly
                              className="pendent-load-details-input small"
                              placeholder="City"
                            />
                            <input
                              type="text"
                              value={point.country || ""}
                              readOnly
                              className="pendent-load-details-input small"
                              placeholder="Country"
                            />
                          </div>
                          <input
                            type="text"
                            value={point.gMapsLink || ""}
                            readOnly
                            className="pendent-load-details-input"
                            placeholder="Google Maps Link"
                          />
                          <input
                            type="text"
                            value={point.coordinates || ""}
                            readOnly
                            className="pendent-load-details-input"
                            placeholder="Coordinates"
                          />
                          <input
                            type="text"
                            value={point.contactPhone || ""}
                            readOnly
                            className="pendent-load-details-input"
                            placeholder="Contact Phone"
                          />
                        </div>

                        <div className="pendent-load-details-toggle-fields-wrapper">
                          <button
                            type="button"
                            className="pendent-load-details-toggle-fields-btn"
                            onClick={() =>
                              setShowMoreDeliveryFields((prev) => ({ ...prev, [index]: false }))
                            }
                          >
                            Hide fields <FiChevronUp className="pendent-load-details-toggle-arrow" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="pendent-load-details-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="pendent-load-details-toggle-fields-btn"
                          onClick={() =>
                            setShowMoreDeliveryFields((prev) => ({ ...prev, [index]: true }))
                          }
                        >
                          + More fields <FiChevronDown className="pendent-load-details-toggle-arrow" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
  
          {/* Edit Button */}
          <div className="pendent-load-details-button-container">
            <button
              type="button"
              className="pendent-load-details-edit-button"
              onClick={() => {
                const navState = { state: { prefill: pendentLoad } };

                if (pendentLoad.specialLoad) {
                  navigate(`/pendentloads/${pendentLoad.id}/edit-special`, navState);
                } else if (pendentLoad.loadBearer) {
                  navigate(`/pendentloads/${pendentLoad.id}/edit-bearer`, navState);
                } else {
                  navigate(`/pendentloads/${pendentLoad.id}/edit`, navState);
                }
              }}
            >
              Edit
            </button>
          </div>
      </div>
    </div>
  );       
};

export default PendentLoadDetails;